import React from 'react';
import { motion } from 'framer-motion';

interface FeatureItem {
  title: string;
  description: string;
  metric?: string;
  imgSrc: string;
}

const featureData: FeatureItem[] = [
  {
    title: "Pan-India Bulk Supply",
    description: "Multi-ton capacity provider of premium Fresh Button Mushrooms and high-grade Dried Oyster Mushrooms, backed by cold-chain logistics.",
    metric: "Commercial Scale",
    imgSrc: "/images/fresh.webp"
  },
  {
    title: "Global Export Infrastructure",
    description: "Strict international quality standards and phytosanitary compliance for global distribution of premium Dried Oyster Mushrooms.",
    metric: "SGS Compliant",
    imgSrc: "/images/freedelivery.webp" // Consider updating this filename to 'export.webp' later to match
  },
  {
    title: "Agritech Training Network",
    description: "Empowered more than 1,000+ cultivators across Maharashtra with advanced mushroom commercial farming practices and industrial techniques.",
    metric: "1000+ Alumni",
    imgSrc: "/images/cust.webp" // Consider updating this filename to 'training.webp' later to match
  },
  {
    title: "End-to-End Contract Farming",
    description: "Complete infrastructure management across Maharashtra—from commercial laboratory setup and spawn provisioning to final buyback contracts.",
    metric: "Turnkey Setup",
    imgSrc: "/images/sustain.webp"
  }
];

const Features: React.FC = () => {
  return (
    <section className="py-24 bg-neutral-50 relative overflow-hidden" aria-labelledby="features-heading">
      {/* Structural Accent Lines for Premium Enterprise Feel */}
      <div className="absolute top-0 left-1/4 w-px h-full bg-neutral-200/40 hidden md:block" />
      <div className="absolute top-0 left-2/4 w-px h-full bg-neutral-200/40 hidden md:block" />
      <div className="absolute top-0 left-3/4 w-px h-full bg-neutral-200/40 hidden md:block" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <motion.span 
            className="text-xs font-bold tracking-widest text-emerald-700 uppercase bg-emerald-50 px-3 py-1 rounded"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Institutional Capabilities
          </motion.span>
          
          <motion.h2 
            id="features-heading" 
            className="text-3xl md:text-5xl font-bold font-serif text-neutral-900 mt-3 tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Why Enterprises Partner with Us
          </motion.h2>
          
          <div className="w-12 h-1 bg-emerald-600 mx-auto mt-6 rounded" />
        </div>

        {/* Features B2B Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {featureData.map((feature, index) => (
            <motion.div 
              key={index} 
              className="flex flex-col bg-white border border-neutral-200/60 p-8 rounded-2xl shadow-sm hover:shadow-xl hover:border-emerald-500/30 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.15, ease: "easeOut" }}
            >
              {/* Image & Metric Header */}
              <div className="flex items-center justify-between mb-6">
                <motion.div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center bg-neutral-50 border border-neutral-100 shadow-inner"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <img 
                    src={feature.imgSrc} 
                    alt="" 
                    className="w-11 h-11 object-contain rounded" 
                    aria-hidden="true"
                  />
                </motion.div>
                
                {feature.metric && (
                  <span className="text-[11px] font-mono font-semibold text-emerald-700 tracking-wider bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-md">
                    {feature.metric}
                  </span>
                )}
              </div>

              {/* Text Strategy */}
              <h3 className="text-xl font-bold text-neutral-950 mb-3 tracking-wide font-sans">
                {feature.title}
              </h3>
              
              <p className="text-sm text-neutral-600 leading-relaxed font-normal">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;